import React, { useContext, useEffect, useRef, useState } from "react";
import * as GlobalConstants from "../../constants/global";
import { findLinkByAlias } from "../../helpers/GiftCardHelper";
import { CheckBalanceStepModel, ICheckBalanceStep } from "../../models/CheckBalanceStep.model";
import ILink from "../../models/core/Link.interface";
import { IBaseStepProps } from "../../models/props/BaseStepProps.interface";
import { RegiftCardContext } from "../../pages/RegiftCardPage";
import DataService from "../../services/DataService.service";
import { AppSettingContext } from "../Application";
import ErrorComponent from "../shared/ErrorComponent";
import LoadingComponent from "../shared/LoadingComponent";

interface ICheckMyBalanceStepProps extends IBaseStepProps {
    handleCheckBalance(model: ICheckBalanceStep): void;
    handleSubmit(model: ICheckBalanceStep): void;
}

const CheckMyBalanceStep = (props: ICheckMyBalanceStepProps) => {
    const { checkBalanceStep } = useContext(RegiftCardContext);
    const { debugMode, labels, links, reCaptchaSettings } = useContext(AppSettingContext);
    const checkboxRef = useRef(null);

    //#region STATE PROPERTIES

    const [culture, setCulture] = useState('en-GB');
    const [pageModel, setPageModel] = useState<ICheckBalanceStep>(undefined);
    const [hasToShowBalance, setHasToShowBalance] = useState(false);
    const [formattedBalance, setFormattedBalance] = useState("");
    const [redeemCodeLink, setRedeemCodeLink] = useState<ILink>({
        url: "https://redeem.ovationincentives.com/",
        alias: "",
        targetBlank: false,
        title: "Redeem"
    });

    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState(null);

    const [isButtonWaiting, setIsButtonWaiting] = useState(false);
    const [checkCardErrorMessage, setCheckCardErrorMessage] = useState("");

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (checkboxRef && checkboxRef.current) {
            checkboxRef.current.checked = true;
        }
    });

    useEffect(() => {
        setIsLoading(true);
        setCulture(document.documentElement.lang);

        const redeemLink = findLinkByAlias(GlobalConstants.PAGE_LINKS.REDEEM_CODE, links);
        if (redeemLink && redeemLink.url.trim()) {
            setRedeemCodeLink(redeemLink);
        }

        setPageModel({ ...checkBalanceStep });
    }, []);

    useEffect(() => {
        setIsLoading(false);

        if (hasToShowBalance) {
            let balance = labels.get("GiftCard.App.Steps.CheckBalance.BalanceRemaining");
            balance = balance.replace("{0}", `${pageModel.currencySymbol}${pageModel.remainingBalance.toFixed(2)}`);
            setFormattedBalance(balance);
        }

        props.handleDirtyState(isModelDirty());
    }, [pageModel]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = () => {
        if (checkboxRef.current.checked) {
            props.handleBeginEdit();
        }
    };

    const handleCheckBalance = () => {
        setIsButtonWaiting(true);
        setCheckCardErrorMessage("");

        // @ts-ignore
        window.grecaptcha.ready(() => {
            // @ts-ignore
            window.grecaptcha.execute(
                reCaptchaSettings.reCaptchaPublicKey, {
                action: 'check_balance'
            }).then(async (token: string) => {
                // proceed with check balance
                let baskedId = localStorage.getItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID);
                if (!baskedId) {
                    baskedId = "";
                }

                DataService.checkCardBalance(culture, baskedId, pageModel.giftCardCode, token)
                    .then(response => {
                        if (typeof response === "string") {
                            setCheckCardErrorMessage(response);
                        } else {
                            if (response && response.id && response.id > 0) {
                                const copyModel = { ...pageModel };
                                copyModel.currencySymbol = response.currencySymbol;
                                copyModel.remainingBalance = response.amount;
                                copyModel.redeemCode = response.redeemCode;

                                props.handleCheckBalance(copyModel);

                                setHasToShowBalance(true);
                                setPageModel(copyModel);
                                setIsButtonWaiting(false);
                            } else {
                                setCheckCardErrorMessage(labels.get("GiftCard.App.Steps.CheckBalance.Error.InvalidCode"));
                                setTimeout(() => {
                                    setIsButtonWaiting(false);
                                }, 1000);
                            }
                        }
                    }).catch(error => {
                        console.error(error);
                        setPageError("An error occurred while checking giftcard code.");
                    });
            });
        });
    };

    const handleCardCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasToShowBalance(false);
        setPageModel({
            ...pageModel,
            giftCardCode: e.target.value.toUpperCase()
        });
    };

    const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        props.handleSubmit({
            currencySymbol: pageModel.currencySymbol,
            giftCardCode: pageModel.giftCardCode,
            remainingBalance: pageModel.remainingBalance,
            redeemCode: pageModel.redeemCode,
            isDirty: false
        });

        checkboxRef.current.checked = false;
    };

    //#endregion

    //#region COMMON HELPERS

    const isValid = (): boolean => {
        return CheckBalanceStepModel.validateModel(pageModel);
    };

    const isModelDirty = (): boolean => {
        if (!pageModel) {
            return false;
        }

        if (pageModel.giftCardCode != checkBalanceStep.giftCardCode) {
            return true;
        }

        if (pageModel.remainingBalance != checkBalanceStep.remainingBalance) {
            return true;
        }

        if (pageModel.currencySymbol != checkBalanceStep.currencySymbol) {
            return true;
        }

        return false;
    }

    //#endregion

    return (
        <>
            {pageError ? (
                <ErrorComponent />
            ) : (
                <div className="my-balance box">
                    <input
                        ref={checkboxRef}
                        defaultChecked={true}
                        onChange={handleExpandBoxCheck}
                        type="checkbox"
                        name="my-balance-box-hide"
                        id="my-balance-box-hide"
                        data-my-balance-box-hide
                    />
                    <div className="action-card" style={{ display: 'grid' }}>
                        <h3>{labels.get("GiftCard.App.Steps.CheckBalance")}</h3>
                        <div className="edit">
                            <label htmlFor="my-balance-box-hide" className="label-fs">
                                <i></i>
                            </label>
                        </div>
                        <div className="show" >
                            {(isLoading) ? (
                                <LoadingComponent />
                            ) : (
                                <div className="slide-content" >
                                    {debugMode &&
                                        <div>
                                            <p>Card code: {pageModel.giftCardCode}</p>
                                            <p>Balance: {pageModel.remainingBalance}</p>
                                            <p>Currency: {pageModel.currencySymbol}</p>
                                            <p>Has to show balance: {String(hasToShowBalance)}</p>
                                            <br />
                                            <p>Is dirty: {String(isModelDirty())}</p>
                                            <p>Is valid: {String(isValid())}</p>
                                        </div>
                                    }

                                    <div className="flex-container">
                                        <div className="form-field">
                                            <label htmlFor="balance__options_card_code" className="label-fs">{labels.get("GiftCard.App.Steps.CheckBalance.Input.Title")}</label>
                                            <input type="text"
                                                maxLength={25}
                                                value={pageModel.giftCardCode}
                                                onInput={handleCardCodeChange}
                                                name="balance__options_card_code" id="balance__options_card_code" placeholder={labels.get("GiftCard.App.Steps.CheckBalance.Input.Placeholder")} />
                                        </div>
                                        <div className="form-field" style={{ display: 'flex' }}>
                                            <button
                                                className="btn btn__primary btn__medium medium-fs"
                                                style={{ alignSelf: 'end' }}
                                                disabled={!isValid() || !isModelDirty() || isButtonWaiting}
                                                onClick={handleCheckBalance}
                                            >
                                                {isButtonWaiting ? (
                                                    <LoadingComponent extraSmall={true} />
                                                ) : (
                                                    labels.get("GiftCard.App.Steps.CheckBalance.Btn.CheckBalance")
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {checkCardErrorMessage && (
                                        <div className="react-input-error-message">
                                            {checkCardErrorMessage}
                                        </div>
                                    )}

                                    {hasToShowBalance &&
                                        <div className={pageModel.remainingBalance > 0 ? "balance balance--active fadeIn" : "balance balance--inactive fadeIn"}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 4.88889V15M8 4.88889C8 4.88889 8 3.72784 8 3.33333C8 2.47422 8.69645 1.77778 9.55556 1.77778C10.4147 1.77778 11.1111 2.47422 11.1111 3.33333C11.1111 4.19244 10.4147 4.88889 9.55556 4.88889C9.09095 4.88889 8 4.88889 8 4.88889ZM8 4.88889C8 4.88889 8 3.38227 8 2.94444C8 1.87056 7.12944 1 6.05556 1C4.98167 1 4.11111 1.87056 4.11111 2.94444C4.11111 4.01833 4.98167 4.88889 6.05556 4.88889C6.69162 4.88889 8 4.88889 8 4.88889ZM2.55556 8H13.4444M2.55556 8C1.69645 8 1 7.30355 1 6.44444C1 5.58533 1.69645 4.88889 2.55556 4.88889H13.4444C14.3036 4.88889 15 5.58533 15 6.44444C15 7.30355 14.3036 8 13.4444 8M2.55556 8L2.55556 13.4444C2.55556 14.3036 3.252 15 4.11111 15H11.8889C12.748 15 13.4444 14.3036 13.4444 13.4444V8" stroke="#22C55E" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <p>{formattedBalance}</p>
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 11L11 1M1 1L11 11" stroke="#22C55E" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    }

                                    <h5>{labels.get("GiftCard.App.Steps.CheckBalance.DescriptionTitle")}</h5>
                                    <p className="small-fs">{labels.get("GiftCard.App.Steps.CheckBalance.DescriptionBody")}</p>
                                    <button
                                        className="btn btn__full_width btn__primary large-fs btn__large"
                                        disabled={(!isValid() || !hasToShowBalance || pageModel.remainingBalance <= 0)}
                                        onClick={handleSubmit}
                                    >
                                        {labels.get("GiftCard.App.Steps.CheckBalance.Btn.Regift")}
                                    </button>

                                    {pageModel.redeemCode.trim() && (
                                        <a
                                            className="btn btn__full_width btn__outlined large-fs btn__large redeem"
                                            href={redeemCodeLink.url.replace("{UVCode}", pageModel.redeemCode)}
                                            target={redeemCodeLink.targetBlank ? "_blank" : "_self"}
                                            rel={redeemCodeLink.targetBlank ? "noreferrer" : ""}
                                        >
                                            {labels.get("GiftCard.App.Steps.CheckBalance.Btn.Redeem")}
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CheckMyBalanceStep;